import React, { useRef, useState } from 'react'
import { Button, TextField, Stack, FormControl, FormControlLabel, RadioGroup, Radio, Tooltip } from '@mui/material';
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import TestData from './TestData';
import { useTranslation } from 'react-i18next';
import WorkflowSessionModal1 from '../../WorkflowSessionModal1';
import ReplacementModal from './ReplacementModal';
import CommonGrid from '../../../Repository/CommonGrid';
import { useEffect } from 'react';
import { api } from '../../../../redux/api';
import { useDispatch, useSelector } from 'react-redux';
import ErrorModal from '../../../ItemProperties/Forms/FieldDataValidation/ErrorModal';
import { debounce } from 'lodash';
import LightTooltip from '../../../../commonComponents/LightTooltip';
import { customDecoding, customEncoding, stripTrailingWorkflowFieldSep } from '../../../../utils/utilFunctions';

const Replacement = ({ selectedNode, handReplaceAndStrip, setHelpDocPath, replacements, updateLength, strips, currTab }) => {
  const { t } = useTranslation();
  const { account } = useSelector((state) => state.queryParams);
  const dispatch = useDispatch();
  const currentRow = useRef(null);
  const containerRef = useRef(null);
  const [modalStates, setModalStates] = useState({
    openModal: false,
    type: 0
  });
  const [valueToReplace, setValueToReplace] = useState('');
  const [replacementValue, setReplacementValue] = useState('');
  const [radioValue, setRadioValue] = useState(t('[VERBATIM]'));
  const [value, setValue] = useState('');
  const [openInputModal, setOpenInputModal] = useState(false);
  const [replace, setReplace] = useState('');
  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState('');
  const [testData, setTestData] = useState('');
  const [testResult, setTestResult] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [testReplace, setTestReplace] = useState('');
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [rowDataLength, setRowDataLength] = useState(0);
  const replaceCrlfValue = useRef('');

  const handleErrorModal = () => {
    setErrorModal(false);
    setErrorMessage('');
  }

  const valueHandler = (selectedValues , previousValue, isFinishClicked) => {
    // Build SWT-compatible tokens, e.g. %WF_DATA,1,DATA_FOUND%
    const valuesArray = Array.isArray(selectedValues) ? selectedValues : [];

    const tokens = valuesArray.map((item) => {
      // Account/system data from wizard
      if (item?.type === 'ACCOUNT_DATA') {
        return `%ACCOUNT_DATA,${item.MAPNAME || ''}%`;
      }
      if (item?.type === 'SYSTEM_DATA') {
        return `%SYSTEM_DATA,${item.MAPNAME || ''}%`;
      }

      // Workflow / calling workflow session variables
      const location = item?.WF_ITEM_LOCATION || 'WF_DATA';
      const mapName = item?.MAPNAME || '';

      // SWT-style: WF_DATA and CWF_DATA include item id in the middle
      // e.g. %WF_DATA,1,DATA_FOUND%
      if (location === 'WF_DATA' || location === 'CWF_DATA') {
        const itemId = item?.WF_ITEM_ID ?? '';
        return `%${location},${itemId},${mapName}%`;
      }

      // Others remain 2-part tokens: %LOCATION,MAPNAME%
      return `%${location},${mapName}%`;
    }).join('');

    // Append to any existing content coming from the field,
    // so multiple wizard runs behave like SWT and concatenate tokens.
    const finalString = (previousValue || '') + tokens;
    setValue(finalString);
    if (replace === 'VAL_TO_REPLACE') {
      setValueToReplace(finalString);
      handleInputChange('REPLACE', finalString);
    }
    if (replace === 'REPLACEMENT_VAL') {
      setReplacementValue(finalString);
      handleInputChange('REPLACEMENT', finalString);
    }
  }

  const handleString = (str) => {
    return str.replace("Replace:", "").replace("With:", "").trim().replace(/\s+/g, ' ')
  }

  const convertPrevStrips = () => {
    let strip = '';
    if (strips.length) {
      for (let i = 0; i < strips.length; i++) {
        strip +=
          customEncoding(customDecoding(stripTrailingWorkflowFieldSep(strips[i]))) + "%1F" + "%1E";
      }
    }
    return strip;
  }

  const updateAsStrip = (newRows) => {
    let stripping = '';
    for (let i = 0; i < (newRows?.length || 0); i++) {
      let value = newRows[i]?.value;

      if (value) {
        if (value.endsWith('(Reg Ex)')) {
          value = value.replace(/\s*\(\s*Reg\s*Ex\s*\)\s*$/, '');
          if (value) { 
            stripping += "%1D" + value + "%1F" + "%1E";
          }
        } else {
          value = value.replace(/\s*\(\s*Verbatim\s*\)/, '');
          if (value) {
            stripping += customEncoding(value) + "%1F" + "%1E";
          }
        }
      }
    }
    const prevStrip = convertPrevStrips();
    const newStrip = prevStrip !== '' ? prevStrip + stripping : stripping;
    handReplaceAndStrip({ type: 'STRIP', strip: newStrip });
  }


  const updateAsReplacement = (updatedRowData) => {
    let replacement = '';
    for (let i = 0; i < updatedRowData?.length; i++) {
      if (updatedRowData[i].value?.endsWith('(Reg Ex)')) {
        const temp = updatedRowData?.[i].value?.match(/Replace:(.*)\sWith:(.*)\s\(Reg Ex\)/s);
        replacement += "%1D" + customEncoding(temp[1]) + "%1F" + customEncoding(temp[2]) + "%1E";
      } else if (updatedRowData[i].value?.endsWith('(XPath)')) {
        const temp = updatedRowData?.[i].value?.match(/Replace:(.*)\sWith:(.*)\s\(XPath\)/s);
        replacement += "%1C" + customEncoding(temp[1]) + "%1F" + customEncoding(temp[2]) + "%1E";
      } else if (updatedRowData[i].value?.endsWith('(JSON Path)')) {
        const temp = updatedRowData?.[i].value?.match(/Replace:(.*)\sWith:(.*)\s\(JSON Path\)/s);
        replacement += "%1B" + customEncoding(temp[1]) + "%1F" + customEncoding(temp[2]) + "%1E";
      } else {
        const temp = updatedRowData?.[i].value?.match(/Replace:(.*)\sWith:(.*)\s\(Verbatim\)/s);
        
        // Extract the values
        let replaceValue = temp?.[1] || '';
        let withValue = temp?.[2] || '';
        
        // For literal \n and \r strings (not actual newlines), use special encoding
        let encodedReplace = replaceValue === '\\n' ? '%5Cn' : 
                            replaceValue === '\\r' ? '%5Cr' : 
                            customEncoding(replaceValue);
        
        let encodedWith = withValue === '\\n' ? '%5Cn' : 
                         withValue === '\\r' ? '%5Cr' : 
                         customEncoding(withValue);
        
        // Handle special case for empty replace value with \r replacement (CRLF pattern)
        if (replaceValue === '' && withValue === '\\r') {
          encodedReplace = '%5Cn';
        }
        if (withValue === '' && replaceValue === '\\r') {
          encodedWith = '%5Cn';
        }
        
        replacement += "" + encodedReplace + new String("%1F") + encodedWith + "%1E";
      }
    }
    handReplaceAndStrip({ type: 'REPLACE', replace: replacement })
  }

  const handleClose2 = (value = false, isOpenModal) => {
    setModalStates((prev) => {
      return {
        openModal: value,
        type: prev.type,
      }
    })
  }

  const handleHelp = (value) => {
    switch (value) {
      case (value === t('[VERBATIM]') || t('[REG_EX]')):
        setHelpDocPath('regex.html')
        break;

      case t('[XPATH]'):
        setHelpDocPath('xpathHelp.html')
        break;

      case t('[JSONPATH]'):
        setHelpDocPath('jsonpathHelp.html')
        break;

      default:
        setHelpDocPath('regex.html')
        break;
    }
  }

  const handleValToReplaceModal = (val) => {
    setOpenInputModal(false);
    setValueToReplace(val);
    // Also trigger grid update when modal is closed
    const newStr = updateValueByChanges('REPLACE', val, val, replacementValue);
    updateGridWithNewValue(newStr);
  }

  const handleReplacementModal = (val) => {
    setOpenInputModal(false);
    setReplacementValue(val);
    // Also trigger grid update when modal is closed
    const newStr = updateValueByChanges('REPLACEMENT', val, valueToReplace, val);
    updateGridWithNewValue(newStr);
  }

  const handleDefaultValuesForFields = () => {
    if (currentRow.current) {
      const res = currentRow.current?.value?.split(':')
      if (res[1]?.split(' ')[0]) {
        const replaceValue = res[1]?.split(' With')[0];
        // Preserve literal \n and \r strings
        setValueToReplace(replaceValue);
      } else setValueToReplace('');

      if (res[2]?.split(' ')[0]) {
        const replacementVal = res[2]?.split(/\s\((Verbatim|Reg Ex|XPath|JSON Path)\)/)[0];
        // Preserve literal \n and \r strings
        setReplacementValue(replacementVal);
      } else setReplacementValue('');

      const replaceType = {
        [t('[VERBATIM]')] : t('[VERBATIM]'),
        [t('[REG_EX]')] : t('[REG_EX]'),
        [t('[XPATH]')] : t('[XPATH]'),
        [t('[JSONPATH]')] : t('[JSONPATH]'),
      }
      Object.keys(replaceType).forEach((e) => {
        if (currentRow?.current?.value?.includes(e))setRadioValue(e);
      })
    }
  }

  const customClickHandlerOnAdd = (e, rowIndex) => {
    const newRowData = [];
    e.api.forEachNode((node) => {
      const newIndex = newRowData.length;
      newRowData.push({ ...node.data, id: newIndex });
    });
    const newData = { id: newRowData?.length, value: `Replace: With: ${radioValue}` }
    setSelectedIndex(newData.id);
    currentRow.current = newData;
    setTestReplace(newData?.value)
    setValueToReplace('');
    setReplacementValue('');
    setRadioValue(t('[VERBATIM]'));
    newRowData.push(newData);
    setRowData(newRowData);
    e.api.setGridOption('rowData', newRowData)
    setRowDataLength(newRowData?.length);
    updateLength({ type: 'REPLACE', len: newRowData?.length });
  }

  const gridOptions = {
    columnDefs: [
      {
        addNewControl: true,
        addControlLabel: t('MNU_NEW_REPLACEMENT'),
        headerName: t('MNU_NEW_REPLACEMENT'),
        field: "value",
        editable: (params) => !params.data.value,
        cellRenderer: (info) => CustomCellRenderer(info),
        customClickHandlerOnAdd: debounce(customClickHandlerOnAdd, 100),
        rowDrag: true,
      }
    ],
    getRowStyle: (params) => {
      const rowData = params.data;
      if (rowData?.id === currentRow?.current?.id) {
        return { border: '1px solid black' };
      } else {
        return { opacity: '0.5' };
      }
    },
    rowDragManaged: true,
    animateRows: true
  };

  const handleCopy = (info) => {
    const newRowData = [];
    let maxId = 0;

    info.api.forEachNode((node, index) => {
      const newNodeData = { ...node.data };
      if (newNodeData.id > maxId) {
        maxId = newNodeData.id;
      }
      newRowData.push(newNodeData);
    });

    if (currentRow.current) {
      const copiedRow = { ...currentRow.current, id: maxId + 1 };
      newRowData.push(copiedRow);
    }
    setRowData(newRowData);
    info.api.setGridOption('rowData', newRowData)
    updateAsReplacement(newRowData);
    setRowDataLength(newRowData?.length);
    updateLength({ type: 'REPLACE', len: newRowData?.length });

    // Highlight the newly added row
    const newRowId = maxId + 1;
    setSelectedIndex(newRowId - 1)
    if (newRowId !== null) {
      const rowNode = info.api.getRowNode(newRowId - 1);
      if (rowNode) {
        currentRow.current = rowNode.data;
        handleDefaultValuesForFields();
        rowNode.setHighlighted(true);
      }
    }
  }

  const handleDelete = (info) => {
    const newRowData = [];
    // Delete the row that the user actually clicked on,
    // not whatever row happens to be stored in currentRow.current.
    const deleteId = info?.data?.id;

    info.api.forEachNode((node, index) => {
      if (node.data.id !== deleteId) {
        newRowData.push({ ...node.data, id: newRowData.length + 1 });
      }
    });
    setRowData(newRowData);
    info.api.setGridOption('rowData', newRowData)
    setRowDataLength(newRowData?.length);
    updateLength({ type: 'REPLACE', len: newRowData?.length });
    updateAsReplacement(newRowData);

    // Decide which row should become the new "current" row after deletion
    let newCurrentRow = null;
    if (newRowData.length > 0) {
      // Prefer the row that shifted into the deleted row's place,
      // otherwise fall back to the last row.
      const candidateIndex = Math.min(info.node.rowIndex, newRowData.length - 1);
      newCurrentRow = newRowData[candidateIndex];
    }

    const firstRowIndex = newCurrentRow ? newCurrentRow.id : null;
    if (firstRowIndex !== null) {
      const rowNode = info.api.getRowNode(firstRowIndex - 1);
      if (rowNode) {
        currentRow.current = rowNode?.data;
        info.api.redrawRows();
        handleDefaultValuesForFields();
      }
    }
  }

  const handleRow = (options) => {
    if (options?.eventPath?.[0]?.classList?.[0]?.includes('DELETE_MUI')) return;
    if (options?.eventPath?.[0]?.classList?.[0]?.includes('FILE_COPY')) return;
    currentRow.current = options?.data;
    setTestReplace(options?.data?.value);
    handleDefaultValuesForFields();
    options.api.redrawRows();
  }

  const CustomCellRenderer = (info) => {
    const { value, data } = info;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: '2rem',
        }}
      >
        {!value || /^\s*$/.test(value) ? (
          <span style={{ color: "#0000008A" }}>{t('MNU_NEW_REPLACEMENT')}</span>
        ) : (
          <div className='truncated-text' title={value}>{value}</div>
        )}
        {data?.key || data?.value ? (
          <div className="flex align-center">
            <LightTooltip
              title={'Copy Replacement'}
              arrow>
              <span style={{ cursor: 'pointer' }} className="FILE_COPY" onClick={() => handleCopy(info)}></span>
            </LightTooltip>
            <LightTooltip
              title={'Delete Replacement'}
              arrow>
              <span style={{ cursor: 'pointer' }} className="DELETE_MUI" onClick={() => handleDelete(info)}></span>
            </LightTooltip>
          </div>
        ) : (
          ""
        )}
      </div>
    );
  };

  const updateValueByChanges = (name, value, currentValueToReplace = valueToReplace, currentReplacementValue = replacementValue) => {
    let newValue = '';
    // Check if we're dealing with CRLF values
    const isValueCRLF = value === 'CRLF' || value === '\\n';
    const isReplacementCRLF = currentReplacementValue === 'CRLF' || currentReplacementValue === '\\r';
    const isValueToReplaceCRLF = currentValueToReplace === 'CRLF' || currentValueToReplace === '\\n';
    
    if (replaceCrlfValue.current !== '' || isValueCRLF || isReplacementCRLF || isValueToReplaceCRLF) {
      if (name === 'REPLACE') {
        const displayValue = (value === 'CRLF' || value === '\\n') ? '\\n' : value;
        const displayReplacement = (currentReplacementValue === 'CRLF' || currentReplacementValue === '\\r') ? '\\r' : currentReplacementValue;
        newValue = `Replace:${displayValue} With:${displayReplacement} ${radioValue}`;
      } else if (name === 'REPLACEMENT') {
        const displayValue = (currentValueToReplace === 'CRLF' || currentValueToReplace === '\\n') ? '\\n' : currentValueToReplace;
        const displayReplacement = (value === 'CRLF' || value === '\\r') ? '\\r' : value;
        newValue = `Replace:${displayValue} With:${displayReplacement} ${radioValue}`;
      } else if (name === 'RADIO') {
        const displayValue = (currentValueToReplace === 'CRLF' || currentValueToReplace === '\\n') ? '\\n' : currentValueToReplace;
        const displayReplacement = (currentReplacementValue === 'CRLF' || currentReplacementValue === '\\r') ? '\\r' : currentReplacementValue;
        newValue = `Replace:${displayValue} With:${displayReplacement} ${value}`;
      }
    } else {
      if (name === 'REPLACE') {
        newValue = `Replace:${value} With:${currentReplacementValue} ${radioValue}`
      } else if (name === 'REPLACEMENT') {
        newValue = `Replace:${currentValueToReplace} With:${value} ${radioValue}`
      } else if (name === 'RADIO') {
        newValue = `Replace:${currentValueToReplace} With:${currentReplacementValue} ${value}`
      }
    }
    return newValue;
  }

  const updatingData = (rows) => {
    // const withReplaceValue = [];
    // const withOutReplceValue = [];
    // for (let i = 0; i < rows.length; i++) {
    //   const val = rows[i]?.value?.split(' ')[1].split(':')[1];
    //   if (val === '') {
    //     if (['(Verbatim)', '(Reg Ex)'].some(keyword => rows[i].value.includes(keyword))) {
    //       withOutReplceValue.push(rows[i]);
    //     }
    //   } else {
    //     withReplaceValue.push(rows[i]);
    //   }
    // }

    // if (withOutReplceValue.length !== 0) {
    //   const cleanedData = withOutReplceValue?.map(obj => {
    //     const cleanedValue = obj.value.replace(/Replace:|With:/g, '').trim().replace(/\s+/g, '');
    //     return { ...obj, value: cleanedValue };
    //   });
    //   updateAsStrip(cleanedData);
    // } else updateAsStrip([]);
    // if (withReplaceValue.length !== 0) {
      updateAsReplacement(rows);
    // }
  }

  const updateGridWithNewValue = (newStr) => {
    let updatedRowData = [];
    gridApi.forEachNode(node => {
      if (node?.data.id === currentRow?.current?.id) {
        // Store the complete value without removing \n - we need it for encoding
        const newNode = { ...node.data, value: newStr };
        updatedRowData.push(newNode);
        setTestReplace(newStr);
      } else {
        updatedRowData.push(node.data);
      }
    });
    updatingData(updatedRowData);
    gridApi.setGridOption('rowData', updatedRowData)
    setRowDataLength(updatedRowData?.length);
    updateLength({ type: 'REPLACE', len: updatedRowData?.length });
  }

  const handleInputChange = (name, value) => {
    let newStr = updateValueByChanges(name, value);
    updateGridWithNewValue(newStr);
  }

  const getTestData = (val) => {
    setTestData(val);
  }

  const createTestingRequest = () => {
    const replacements = [];

    const parseRow = (row) => {
      if (typeof row !== 'string') return null;
      const m = row.match(/^Replace:(.*)\sWith:(.*)\s(\(.+\)|\[[^\]]+\])$/s);
      if (!m) return null;
      const [, replaceValRaw, withValRaw, suffixRaw] = m;
      const replaceVal = (replaceValRaw ?? '').trim();
      const withVal = (withValRaw ?? '').trim();
      const suffix = (suffixRaw ?? '').trim();

      // Suffix can be either "(JSON Path)" style or "[JSONPATH]" style (localized).
      let type = 'VERBATIM';
      if (suffix.includes('Reg Ex') || suffix.includes('[REG_EX]')) type = 'REG_EX';
      else if (suffix.includes('XPath') || suffix.includes('[XPATH]')) type = 'XPATH';
      else if (suffix.includes('JSON Path') || suffix.includes('[JSONPATH]')) type = 'JSONPATH';
      else if (suffix.includes('Verbatim') || suffix.includes('[VERBATIM]')) type = 'VERBATIM';

      return { type, replace: replaceVal, with: withVal };
    };

    gridApi.forEachNode((node) => {
      const parsed = parseRow(node?.data?.value);
      if (parsed) replacements.push(parsed);
    });

    return replacements;
  }

  const recieveTestReqeust = (val) => {
    if (val) {
      if (testData === '\\n') {
        testDataFromServer({ testData: '\\n', replacements: createTestingRequest() });
      } else {
        testDataFromServer({ testData, replacements: createTestingRequest() });
      }
    }
  }

  const testDataFromServer = (data) => {
    setLoading(true);
    const payload = {
      ...data,
      testData: (data?.testData || '').replace(/CRLF/g, "\\n"),
    };
    dispatch(api.endpoints.testGlobalSubstitutionJson.initiate({
      account,
      data: payload,
    }, { forceRefetch: true })).then((response) => {
      if (response && response.data.current_state === 'SUCCESS') {
        setTestResult(response.data.data);
        setLoading(false);
      } else {
        setErrorModal(true);
        setErrorMessage(response.data.message);
        setLoading(false);
      }
    })
  }

  const scrollRows = () => {
    if (gridApi && selectedIndex) {
      const rowNode = gridApi.getRowNode(selectedIndex);
      if (rowNode) {
        rowNode.setHighlighted(true);
        gridApi.ensureIndexVisible(selectedIndex, 'start');
      }
    }
  };

  const handleReplacementModalValue = (val) => {
    if (replace === "REPLACEMENT_VAL") {
      if (val === 'CRLF') {
        // Set the literal string \r, not the actual carriage return character
        setReplacementValue('\\r');
        const newStr = updateValueByChanges('REPLACEMENT', '\\r', valueToReplace, '\\r');
        updateGridWithNewValue(newStr);
      } else {
        setReplacementValue(val);
        const newStr = updateValueByChanges('REPLACEMENT', val, valueToReplace, val);
        updateGridWithNewValue(newStr);
      }
    } else {
      if (val === 'CRLF') {
        // Set the literal string \n, not the actual newline character
        replaceCrlfValue.current = '\\n';
        setValueToReplace('\\n');
        const newStr = updateValueByChanges('REPLACE', '\\n', '\\n', replacementValue);
        updateGridWithNewValue(newStr);
      } else {
        replaceCrlfValue.current = val.includes('\n') ? val : '';
        setValueToReplace(val);
        const newStr = updateValueByChanges('REPLACE', val, val, replacementValue);
        updateGridWithNewValue(newStr);
      }
    }
  }

  const handleDefaultValues = () => {
    if (replacements?.length !== 0) {
      const updatedValues = [];
      for (let i = 0; i < replacements?.length; i++) {
        let id = i + 1;
        if (replacements[i]?.startsWith("%1D")) {
          const regex = replacements[i]?.replace("%1D", "")?.split("%1F");
          if (regex?.length !== 0)
            updatedValues.push({ id, value: `Replace:${customDecoding(regex[0])} With:${customDecoding(regex[1])} ${t('[REG_EX]')}` });
        } else if (replacements[i]?.startsWith("%1C")) {
          const xPathRes = replacements[i]?.replace("%1C", "")?.split("%1F");
          if (xPathRes?.length !== 0)
            updatedValues.push({ id, value: `Replace:${customDecoding(xPathRes[0])} With:${customDecoding(xPathRes[1])} ${t('[XPATH]')}` })
        } else if (replacements[i]?.startsWith("%1B")) {
          const jsonPathRes = replacements[i]?.replace("%1B", "")?.split("%1F")
          if (jsonPathRes?.length !== 0)
            updatedValues.push({ id, value: `Replace:${customDecoding(jsonPathRes[0])} With:${customDecoding(jsonPathRes[1])} ${t('[JSONPATH]')}` })
        } else if (replacements[i]?.startsWith("%5Cn")) {
          // Handle the SWT format for CRLF: %5Cn%1F%5Cr%1E
          const crlfRes = replacements[i]?.replace("%5Cn", "")?.split("%1F");
          if (crlfRes?.length !== 0) {
            const decodedFirst = crlfRes[0] === "" ? "\\n" : customDecoding(crlfRes[0]);
            const decodedSecond = crlfRes[1]?.replace("%5Cr", "") === "" ? "\\r" : customDecoding(crlfRes[1]?.replace("%5Cr", ""));
            updatedValues.push({ id, value: `Replace:${decodedFirst} With:${decodedSecond} ${t('[VERBATIM]')}` });
          }
        } else {
          const verbatim = replacements[i]?.split("%1F");
          if (verbatim?.length !== 0 && replacements[i]) {
            // Handle special decoding for literal \n and \r
            const decodedFirst = verbatim[0] === '%5Cn' ? '\\n' : 
                               verbatim[0] === '%5Cr' ? '\\r' : 
                               customDecoding(verbatim[0]);
            const decodedSecond = verbatim[1] === '%5Cn' ? '\\n' : 
                                 verbatim[1] === '%5Cr' ? '\\r' : 
                                 customDecoding(verbatim[1]);
            updatedValues.push({ id, value: `Replace:${decodedFirst} With:${decodedSecond} ${t('[VERBATIM]')}` });
          }
        }
      }
      if (updatedValues?.length !== 0) {
        currentRow.current = updatedValues[0];
        setTestReplace(updatedValues[0]?.value);
        handleDefaultValuesForFields();
        updatingData(updatedValues);
      }
      setRowDataLength(updatedValues?.length);
      updateLength({ type: 'REPLACE', len: updatedValues?.length });
      setRowData(updatedValues)
    }
  }

  const onRowDragEnd = e => {
    const nodes = [];
    e.api.forEachNode((node) => {
      nodes.push(node?.data);
    });
    setRowData(nodes);
    updatingData(nodes);
  };

  useEffect(() => {
    if (containerRef.current && currentRow.current) {
      const itemHeight = 40;
      const containerHeight = containerRef.current.clientHeight;
      const scrollTo = selectedIndex * itemHeight;
      if (scrollTo > containerHeight) {
        containerRef.current.scrollTop = scrollTo;
        setTimeout(() => {
          scrollRows();
        }, 0);
      }
    }
  }, [rowData, selectedIndex]);

  useEffect(() => {
    handleDefaultValues();
  }, [selectedNode])

  useEffect(() => {
    if (currTab === 1) {
      if (radioValue) {
        if (rowDataLength > 1) {
          if (radioValue === t('[VERBATIM]')) {
            setHelpDocPath('adv_replacements.html');
          } else {
            handleHelp(radioValue);
          }
        }
      }
    }
  }, [radioValue, rowDataLength, currTab]);

  return (
    <>
      <div className="padding-16">
        <Stack spacing={"15px"}>
          <div ref={containerRef}>
            <CommonGrid
              gridOptions={gridOptions}
              rowDragManaged={true}
              rowData={rowData}
              getGridAPI={(gridOptions, gridApi) => setGridApi(gridApi.api)}
              containerStyle={{ height: "30vh" }}
              gridProps={{
                onRowClicked: handleRow,
                onRowDragEnd: onRowDragEnd
              }}
            />
          </div>
          {rowDataLength !== 0 && <>
            <FormControl className="width-100" size="small">
              <div className="flex">
                <TextField
                  variant="outlined"
                  size="small"
                  label={t("LBL_VAL_TO_REPLACE")}
                  type="text"
                  className={"width-100"}
                  name={"VAL_TO_REPLACE"}
                  multiline
                  rows={1}
                  value={valueToReplace}
                  autoComplete="off"
                  onChange={(e) => {
                    setValueToReplace(e.target.value);
                    // Pass the current replacement value explicitly to avoid state timing issues
                    const newStr = updateValueByChanges('REPLACE', e.target.value, e.target.value, replacementValue);
                    updateGridWithNewValue(newStr);
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disableElevation
                  className="margin-left-4"
                  sx={{
                    width: "40px",
                    padding: "4px 8px",
                    minWidth: "40px",
                  }}
                  onClick={() => {
                    setReplace('VAL_TO_REPLACE')
                    setOpenInputModal(true)
                  }}>
                  {t('BTN_ABC')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disableElevation
                  className="margin-left-4"
                  sx={{
                    width: "40px",
                    padding: "4px 8px",
                    minWidth: "40px",
                  }}
                  onClick={() => {
                    setReplace('VAL_TO_REPLACE')
                    setModalStates({ openModal: true, type: 0 })
                  }}>
                  <MoreHorizIcon fontSize="small" />
                </Button>
              </div>
            </FormControl>
            <FormControl className="width-100" size="small">
              <div className="flex">
                <TextField
                  variant="outlined"
                  size="small"
                  label={t("LBL_REPLACEMENT_VAL")}
                  type="text"
                  className={"width-100"}
                  name={"REPLACEMENT_VAL"}
                  multiline
                  rows={1}
                  value={replacementValue}
                  autoComplete="off"
                  onChange={(e) => {
                    setReplacementValue(e.target.value);
                    // Pass the current value to replace explicitly to avoid state timing issues
                    const newStr = updateValueByChanges('REPLACEMENT', e.target.value, valueToReplace, e.target.value);
                    updateGridWithNewValue(newStr);
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disableElevation
                  className="margin-left-4"
                  sx={{
                    width: "40px",
                    padding: "4px 8px",
                    minWidth: "40px",
                  }}
                  onClick={() => {
                    setReplace('REPLACEMENT_VAL')
                    setOpenInputModal(true)
                  }}>
                  {t('BTN_ABC')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disableElevation
                  className="margin-left-4"
                  sx={{
                    width: "40px",
                    padding: "4px 8px",
                    minWidth: "40px",
                  }}
                  onClick={() => {
                    setReplace('REPLACEMENT_VAL')
                    setModalStates({ openModal: true, type: 0 })
                  }
                  }>
                  <MoreHorizIcon fontSize="small" />
                </Button>
              </div>
            </FormControl>
            {/* Radio Buttons */}
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              defaultValue="female"
              name="radio-buttons-group"
              value={radioValue || ''}
              onChange={(e) => {
                setRadioValue(e.target.value);
                handleHelp(e.target.value);
                handleInputChange('RADIO', e.target.value);
              }}
            >
              <FormControlLabel value={t('[VERBATIM]')} control={<Radio />} label={t("BTN_USE_VAL_TO_REPLACE_VERBATIM")} />
              <FormControlLabel value={t('[REG_EX]')} control={<Radio />} label={t("BTN_USE_VAL_TO_REPLACE_EXPR")} />
              <FormControlLabel value={t('[XPATH]')} control={<Radio />} label={t("BTN_XPATH")} />
              <FormControlLabel value={t('[JSONPATH]')} control={<Radio />} label={t("BTN_JSON_PATH")} />
            </RadioGroup>
          </>}

          <TestData testResult={testResult} getTestData={getTestData} type={'LBL_TEST_REPL_HERE'} sendTestRequest={recieveTestReqeust} isLoading={loading} />
        </Stack>
      </div>
      <WorkflowSessionModal1
        // Prefill the wizard with the text from the field
        // that opened it (value-to-replace or replacement value).
        previousValue={replace === 'VAL_TO_REPLACE' ? valueToReplace : replacementValue}
        showSelection={"true"}
        valueHandler={valueHandler}
        handleClose={handleClose2}
        openModal={modalStates.openModal}
        sourceType="callingWorkflow"
        selection={"single"}
      />
      {replace === "VAL_TO_REPLACE" && (
        <ReplacementModal
          openModal={openInputModal}
          closeModal={handleValToReplaceModal}
          inputValue={valueToReplace}
          handleValue={handleReplacementModalValue}
        />
      )}
      {replace === "REPLACEMENT_VAL" && (
        <ReplacementModal
          openModal={openInputModal}
          closeModal={handleReplacementModal}
          inputValue={replacementValue}
          handleValue={handleReplacementModalValue}
        />
      )}
      {errorModal && <ErrorModal
        errorModal={errorModal}
        handleErrorModal={handleErrorModal}
        errorMessage={errorMessage}></ErrorModal>}
    </>
  )
}

export default Replacement